import { type Control, type FieldValues, type Path, useController } from 'react-hook-form'
import { StyleSheet, View, type ViewProps } from 'react-native'
import { HelperText, Switch, Text } from 'react-native-paper'

export type ToggleProps<T extends FieldValues = Record<string, unknown>> = {
    name: Path<T>
    control: Control<T>
    label: string
    hint?: string
    disabled?: boolean
    wrapperProps?: ViewProps
}

export function Toggle<T extends FieldValues = Record<string, unknown>>({
    name,
    control,
    label,
    hint,
    disabled = false,
    wrapperProps = {},
}: ToggleProps<T>) {
    const {
        field,
        fieldState: { error },
    } = useController({
        name,
        control,
    })

    return (
        <View
            style={[
                styles.inputWrapper,
                styles.textInputWrapper,
                styles.toggleWrapper,
                wrapperProps.style,
            ]}
            {...wrapperProps}
        >
            <View style={styles.toggleContainer}>
                <Text variant="labelLarge" style={styles.toggleLabel}>
                    {label}
                </Text>
                <Switch
                    value={Boolean(field.value)}
                    onValueChange={field.onChange}
                    disabled={disabled}
                    accessibilityLabel={label}
                />
            </View>
            {hint && !error ? <HelperText type="info">{hint}</HelperText> : null}
            {error ? <HelperText type="error">{error.message}</HelperText> : null}
        </View>
    )
}

const styles = StyleSheet.create({
    inputWrapper: {
        marginBottom: 8,
    },
    textInputWrapper: {
        zIndex: -1,
    },
    toggleWrapper: {
        marginBottom: 12,
    },
    toggleContainer: {
        gap: 8,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    toggleLabel: {
        fontWeight: '600',
    },
})
